/**
 * 状态常量定义
 * 
 * 系统中各种状态的常量定义
 * 
 * @author YSFL Team
 * @date 2025-10-01
 */

/**
 * 用户状态
 * 
 * ⚠️ 注意：USER_STATUS_LABELS 已移至 types/user/constants.ts
 * 请从 @bayes-mes/shared/types/user 导入
 */
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  LOCKED: 'locked',
} as const

// USER_STATUS_LABELS 已移至 types/user/constants.ts

/**
 * 工单状态
 *
 * ⚠️ 注意：WORK_ORDER_STATUS_LABELS 已移至 types/production/constants.ts
 * 请从 @bayes-mes/shared/types/production 导入（数字键版本 1-6）
 *
 * 此处保留 WORK_ORDER_STATUS 常量仅用于向后兼容，但不再导出 LABELS
 */
export const WORK_ORDER_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const

// WORK_ORDER_STATUS_LABELS 已移至 types/production/constants.ts

/**
 * 质量等级
 */
export const QUALITY_LEVEL = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
} as const

export const QUALITY_LEVEL_LABELS = {
  [QUALITY_LEVEL.A]: '优秀',
  [QUALITY_LEVEL.B]: '良好',
  [QUALITY_LEVEL.C]: '合格',
  [QUALITY_LEVEL.D]: '不合格',
}

/**
 * API响应码
 */
export const API_CODE = {
  SUCCESS: 0,
  ERROR: 1,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  SERVER_ERROR: 500,
} as const

/**
 * 文件类型
 */
export const FILE_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  EXCEL: 'excel',
  PDF: 'pdf',
} as const

/**
 * 文件后缀与类型映射
 */
export const FILE_EXTENSION_MAP: Record<string, string> = {
  // 图片
  jpg: FILE_TYPE.IMAGE,
  jpeg: FILE_TYPE.IMAGE,
  png: FILE_TYPE.IMAGE,
  gif: FILE_TYPE.IMAGE,
  webp: FILE_TYPE.IMAGE,
  // 视频
  mp4: FILE_TYPE.VIDEO,
  avi: FILE_TYPE.VIDEO,
  mov: FILE_TYPE.VIDEO,
  // 文档
  doc: FILE_TYPE.DOCUMENT,
  docx: FILE_TYPE.DOCUMENT,
  txt: FILE_TYPE.DOCUMENT,
  // Excel
  xls: FILE_TYPE.EXCEL,
  xlsx: FILE_TYPE.EXCEL,
  // PDF
  pdf: FILE_TYPE.PDF,
}

/**
 * 分页默认配置
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
} as const

/**
 * 性别
 * 
 * ⚠️ 注意：GENDER_LABELS 已移至 types/employee/constants.ts
 * 请从 @bayes-mes/shared/types/employee 导入
 */
export const GENDER = {
  UNKNOWN: 0,
  MALE: 1,
  FEMALE: 2,
} as const

// GENDER_LABELS 已移至 types/employee/constants.ts

/**
 * 婚姻状况
 * 
 * ⚠️ 注意：MARITAL_STATUS_LABELS 已移至 types/employee/constants.ts
 * 请从 @bayes-mes/shared/types/employee 导入
 */
export const MARITAL_STATUS = {
  SINGLE: 1,
  MARRIED: 2,
  DIVORCED: 3,
  WIDOWED: 4,
} as const

// MARITAL_STATUS_LABELS 已移至 types/employee/constants.ts

/**
 * 员工在职状态
 * 
 * ⚠️ 注意：EMPLOYMENT_STATUS_LABELS 已移至 types/employee/constants.ts
 * 请从 @bayes-mes/shared/types/employee 导入
 */
export const EMPLOYMENT_STATUS = {
  ACTIVE: 1,
  RESIGNED: 2,
  SUSPENDED: 3,
} as const

// EMPLOYMENT_STATUS_LABELS 已移至 types/employee/constants.ts

/**
 * 启用/禁用状态
 */
export const ENABLE_STATUS = {
  DISABLED: 0,
  ENABLED: 1,
} as const

export const ENABLE_STATUS_LABELS = {
  [ENABLE_STATUS.DISABLED]: '禁用',
  [ENABLE_STATUS.ENABLED]: '启用',
}

/**
 * 菜单类型
 */
export const MENU_TYPE = {
  CATALOG: 'catalog',
  MENU: 'menu',
  BUTTON: 'button',
  EMBEDDED: 'embedded',
  LINK: 'link',
} as const

export const MENU_TYPE_LABELS = {
  [MENU_TYPE.CATALOG]: '目录',
  [MENU_TYPE.MENU]: '菜单',
  [MENU_TYPE.BUTTON]: '按钮',
  [MENU_TYPE.EMBEDDED]: '内嵌',
  [MENU_TYPE.LINK]: '外链',
}

/**
 * 菜单平台
 */
export const MENU_PLATFORM = {
  WEB: 'web',
  APP: 'app',
} as const

export const MENU_PLATFORM_LABELS = {
  [MENU_PLATFORM.WEB]: 'Web端',
  [MENU_PLATFORM.APP]: 'App端',
}

/**
 * 权限类型
 * 
 * ⚠️ 注意：PERMISSION_TYPE_LABELS 已移至 types/permission/constants.ts
 * 请从 @bayes-mes/shared/types/permission 导入
 */
export const PERMISSION_TYPE = {
  MENU: 1,
  BUTTON: 2,
  API: 3,
  DATA: 4,
} as const

// PERMISSION_TYPE_LABELS 已移至 types/permission/constants.ts

/**
 * 数据权限范围
 * 
 * ⚠️ 注意：DATA_SCOPE_LABELS 已移至 types/role/constants.ts
 * 请从 @bayes-mes/shared/types/role 导入
 */
export const DATA_SCOPE = {
  ALL: 'all',
  DEPARTMENT: 'department',
  SELF: 'self',
} as const

// DATA_SCOPE_LABELS 已移至 types/role/constants.ts

/**
 * HTTP方法
 */
export const HTTP_METHOD = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
  HEAD: 'HEAD',
  OPTIONS: 'OPTIONS',
} as const

/**
 * 企业微信状态
 */
export const WEWORK_STATUS = {
  ACTIVATED: 1,
  DISABLED: 2,
  NOT_ACTIVATED: 4,
  QUIT: 5,
} as const

export const WEWORK_STATUS_LABELS = {
  [WEWORK_STATUS.ACTIVATED]: '已激活',
  [WEWORK_STATUS.DISABLED]: '已禁用',
  [WEWORK_STATUS.NOT_ACTIVATED]: '未激活',
  [WEWORK_STATUS.QUIT]: '退出企业',
}

/**
 * 审计日志操作类型
 * 
 * ⚠️ 注意：AUDIT_ACTION_LABELS 已移至 types/audit-log/constants.ts
 * 请从 @bayes-mes/shared/types/audit-log 导入
 */
export const AUDIT_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  VIEW: 'view',
  EXPORT: 'export',
  IMPORT: 'import',
  APPROVE: 'approve',
  REJECT: 'reject',
  SUBMIT: 'submit',
  CANCEL: 'cancel',
} as const

// AUDIT_ACTION_LABELS 已移至 types/audit-log/constants.ts

/**
 * 系统日志级别
 */
export const LOG_LEVEL = {
  EMERGENCY: 'emergency',
  ALERT: 'alert',
  CRITICAL: 'critical',
  ERROR: 'error',
  WARNING: 'warning',
  NOTICE: 'notice',
  INFO: 'info',
  DEBUG: 'debug',
} as const

export const LOG_LEVEL_LABELS = {
  [LOG_LEVEL.EMERGENCY]: '紧急',
  [LOG_LEVEL.ALERT]: '警报',
  [LOG_LEVEL.CRITICAL]: '严重',
  [LOG_LEVEL.ERROR]: '错误',
  [LOG_LEVEL.WARNING]: '警告',
  [LOG_LEVEL.NOTICE]: '注意',
  [LOG_LEVEL.INFO]: '信息',
  [LOG_LEVEL.DEBUG]: '调试',
}

/**
 * 登录方式
 */
export const LOGIN_METHOD = {
  PASSWORD: 'password',
  SMS: 'sms',
  WEWORK_SCAN: 'wework_scan',
  WEWORK_AUTO: 'wework_auto',
} as const

export const LOGIN_METHOD_LABELS = {
  [LOGIN_METHOD.PASSWORD]: '账号密码',
  [LOGIN_METHOD.SMS]: '短信验证码',
  [LOGIN_METHOD.WEWORK_SCAN]: '企业微信扫码',
  [LOGIN_METHOD.WEWORK_AUTO]: '企业微信免登',
}
